import { wplToken } from '@atlassiansox/wpl-token';
import { css } from '@emotion/react';
import { useState } from 'react';
import Select from 'react-select';
import { getColor } from '../foundations';

export const dropdownSelectorContainerStyle = css`
  display: inline-flex;
  flex-direction: column;
  width: 100%;
`;

export const labelStyle = css`
  color: ${getColor('n500')};
  font-size: 0.75rem;
  line-height: 1.1;
  margin-bottom: ${wplToken('space.050')};
  text-align: left;
`;

interface Option {
  value: string;
  label: string;
}

export interface DropdownSelectorProps {
  options: Option[];
  name: string;
  label: string;
  id?: string;
  testId?: string;
  onChangeHandler?: (value: string | undefined) => void;
  className?: string;
  defaultValue?: Option;
}

export const DropdownSelector = ({
  options,
  name,
  id,
  testId,
  label,
  onChangeHandler,
  defaultValue,
  className
}: DropdownSelectorProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const elementID = id ? id : `${name}-dropdown`;

  return (
    <div className={className} css={dropdownSelectorContainerStyle}>
      <label css={labelStyle} htmlFor={elementID}>
        {label}
      </label>
      <div
        className="select-dropdown-container"
        data-testid={testId ? testId : elementID}
      >
        <Select<Option>
          defaultValue={defaultValue || { label: 'Select...', value: '' }}
          options={options}
          unstyled={true}
          inputId={elementID}
          onChange={(selectedOption) => {
            onChangeHandler?.(selectedOption?.value || '');
          }}
          onMenuOpen={() => setIsMenuOpen(true)}
          onMenuClose={() => setIsMenuOpen(false)}
          styles={{
            container: (provided) => ({
              ...provided,
              appearance: 'none',
              flexDirection: 'row',
              fontSize: '1rem',
              lineHeight: '1.2',
              textAlign: 'left'
            }),

            singleValue: (provided) => ({
              ...provided,
              marginLeft: wplToken('space.025'),
              marginRight: wplToken('space.025')
            }),

            placeholder: (provided) => ({
              ...provided,
              marginLeft: wplToken('space.025'),
              marginRight: wplToken('space.025')
            }),

            valueContainer: (provided) => ({
              ...provided,
              paddingLeft: wplToken('space.075'),
              paddingRight: wplToken('space.075')
            }),

            menu: (provided) => ({
              ...provided,
              marginTop: wplToken('space.050'),
              boxShadow: '0px 3px 5px 0px #091E4233',
              background: getColor('n0'),
              padding: 0,
              borderRadius: wplToken('space.050')
            }),

            control: (provided, state) => ({
              ...provided,
              'cursor': 'pointer',
              'backgroundColor': isMenuOpen
                ? getColor('n700')
                : getColor('n10'),
              'color': isMenuOpen ? getColor('n0') : getColor('n800'),
              'height': '2.65rem',
              'border': state.isFocused
                ? `2px solid ${getColor('b100')}`
                : `2px solid #dfe1e6`,
              'borderRadius': wplToken('space.050'),
              '&:hover': {
                background: isMenuOpen ? undefined : getColor('n20')
              }
            }),

            dropdownIndicator: (provided, state) => ({
              ...provided,
              transition: 'all .2s ease',
              transform: state.selectProps.menuIsOpen
                ? 'rotate(180deg)'
                : undefined
            }),

            indicatorsContainer: (provided) => ({
              ...provided,
              paddingTop: wplToken('space.075'),
              paddingBottom: wplToken('space.075'),
              paddingLeft: wplToken('space.025'),
              paddingRight: wplToken('space.075')
            }),

            option: (provided, state) => ({
              ...provided,
              'cursor': 'pointer',
              'padding': `${wplToken('space.100')} ${wplToken('space.200')}`,
              'whiteSpace': 'pre-wrap',
              'overflowWrap': 'break-word',
              'backgroundColor': state.isSelected
                ? getColor('b75')
                : state.isFocused
                ? getColor('n30')
                : getColor('n10'),
              'color': state.isSelected ? getColor('b300') : getColor('n800'),
              '&:hover': {
                background: state.isSelected ? getColor('b75') : getColor('n30')
              },
              '&:active': {
                background: getColor('n40')
              }
            }),

            input: (provided) => ({
              ...provided,
              color: 'transparent'
            })
          }}
        />
      </div>
    </div>
  );
};
